import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, Router } from '@angular/router';
import { DEFAULT_VIEW_URL } from '@shared/modules/app/helpers/redirect-unmatched-route';
import { LogLevel } from '@shared/modules/logs/common/constants/log-level';
import { LoggerService } from '@shared/modules/logs/services/logger.service';
import { filter } from 'rxjs/operators';

/**
 * A navigation that throws (a failing lazy chunk, a rejected guard, a malformed URL) leaves the user
 * on whatever was rendered before - in practice a blank screen on app start. Log it and fall back to
 * the default view instead of leaving the app stuck (FE-299).
 *
 * Only for the main app shells; the dynamic-dialog window has its own routes, where sending the user
 * to the inbox would be wrong.
 */
export function provideNavigationErrorRecovery(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const router: Router = inject(Router);
      const loggerService: LoggerService = inject(LoggerService);

      router.events
        .pipe(
          filter((event): event is NavigationError => event instanceof NavigationError),
          takeUntilDestroyed()
        )
        .subscribe((event: NavigationError) => {
          loggerService.error({
            error: event.error,
            log: {
              message: `Navigation to ${event.url} failed`,
              level: LogLevel.ERROR
            }
          });

          // Redirecting to the URL that just failed would only fail again.
          if (event.url !== DEFAULT_VIEW_URL) {
            router.navigateByUrl(DEFAULT_VIEW_URL);
          }
        });
    })
  ]);
}
